// REPORTDASH1 (docs/build-briefs/REPORTDASH1-reports-dashboard.md; Kerry 2026-10-24, "B + C's Needs attention", the same style in all
// three consoles): the pure half of the Reports dashboard. No DOM and no requests. What a figure, a preview and an attention row
// are, how a person's layout is kept and changed, and the words each console's data becomes. The three consoles differ only in
// their `DashboardSource` (which components exist and how their data becomes these shapes); the drawing is one component.
import { MONTH_NAMES } from "./dateText.ts";
import { record } from "./cannedReportModel.ts";

export const MAX_FIGURES = 6;
export const MAX_PREVIEWS = 6;
export const ATTENTION_LIMIT = 5;

/** Where a tap goes. A canned report opens with the dates it shows; an external card is a location console view; an action is the host's own screen. */
export type OpenTarget =
  | { kind: "canned"; id: string; preset?: string; range?: { from: string; to: string } }
  | { kind: "external"; id: string }
  | { kind: "action"; id: string };

export type ChipKind = "open" | "exact" | "live" | "count" | "partial";
/** Words and an icon, never color alone (DEC-533): `kind` picks the icon. */
export interface Chip {
  kind: ChipKind;
  text: string;
}

/** ready: a real figure. empty: nothing to show yet, with the real next step (never $0.00). failed: the read failed (never zero). */
export type TileState = "ready" | "empty" | "failed";

export interface FigureView {
  state: TileState;
  value: string;
  /** The period or basis, one short line. */
  context: string;
  chip?: Chip;
  emptyTitle: string;
  emptyText: string;
}
export type PreviewBody =
  | { kind: "chart"; points: Array<{ label: string; value: number; highlight: boolean }>; first: string; last: string; summary: string }
  | { kind: "table"; columns: string[]; rows: string[][] };
export interface PreviewView {
  state: TileState;
  /** Plain labels: the period and what is measured. */
  filters: string[];
  body?: PreviewBody;
  chip?: Chip;
  /** The dates the preview shows, so a tap opens its report with the same dates (used by a `custom` target). */
  range?: { from: string; to: string };
  emptyTitle: string;
  emptyText: string;
}
export interface AttentionRow {
  id: string;
  cause: string;
  consequence: string;
  /** Present only when this person may open the place that fixes it. Otherwise `whoCanFix` says who can. */
  action?: { label: string; target: OpenTarget };
  whoCanFix?: string;
}

export interface FigureDef {
  id: string;
  title: string;
  /** One line in Customize: where the number comes from. */
  hint: string;
  /** The first target this person can open is used; a figure with none they can open is not shown. */
  open: OpenTarget[];
}
export interface PreviewDef {
  id: string;
  title: string;
  hint: string;
  open: OpenTarget[];
}

export interface DashLoad {
  /** One line under the title: scope and date, named once. */
  scope: string;
  /** "failed" when the attention read failed: one honest line, never silence. */
  attention: AttentionRow[] | "failed";
  /** A key is present only for a component this person may see. */
  figures: Record<string, FigureView>;
  previews: Record<string, PreviewView>;
}

export interface DashboardSource {
  /** Keeps one person's layout apart from another's: console, tenant or network, and the person. */
  storageKey: string;
  figures: readonly FigureDef[];
  previews: readonly PreviewDef[];
  defaults: { figures: readonly string[]; previews: readonly string[] };
  load(): Promise<DashLoad>;
  /** The saved one-number reports this person pinned (CRB10), run through the existing tile routes. Location and franchisor only. */
  tiles?: boolean;
}

// ---------------------------------------------------------------------------------------------------------------------
// Layout, per person
// ---------------------------------------------------------------------------------------------------------------------

export interface Layout {
  figures: string[];
  previews: string[];
}
const LAYOUT_VERSION = 1;

export function defaultLayout(source: Pick<DashboardSource, "defaults">): Layout {
  return { figures: [...source.defaults.figures], previews: [...source.defaults.previews] };
}
function ids(value: unknown, known: ReadonlySet<string>, max: number): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  for (const entry of value) if (typeof entry === "string" && known.has(entry) && !seen.has(entry)) seen.add(entry);
  return [...seen].slice(0, max);
}
/** The stored layout, or the starter set when there is none or it cannot be read. Unknown ids drop out; nothing here throws. */
export function readLayout(raw: string | null, source: DashboardSource): Layout {
  if (raw === null) return defaultLayout(source);
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!record(parsed) || parsed["v"] !== LAYOUT_VERSION) return defaultLayout(source);
    const figures = ids(parsed["figures"], new Set(source.figures.map((def) => def.id)), MAX_FIGURES);
    const previews = ids(parsed["previews"], new Set(source.previews.map((def) => def.id)), MAX_PREVIEWS);
    return figures === undefined || previews === undefined ? defaultLayout(source) : { figures, previews };
  } catch {
    return defaultLayout(source);
  }
}
export function writeLayout(layout: Layout): string {
  return JSON.stringify({ v: LAYOUT_VERSION, figures: layout.figures, previews: layout.previews });
}
export function layoutKey(storageKey: string): string {
  return `rxdash:v${LAYOUT_VERSION}:${storageKey}`;
}
export function moveId(list: readonly string[], id: string, direction: -1 | 1): string[] {
  const at = list.indexOf(id);
  const to = at + direction;
  if (at === -1 || to < 0 || to >= list.length) return [...list];
  const next = [...list];
  next[at] = next[to] as string;
  next[to] = id;
  return next;
}
export function removeId(list: readonly string[], id: string): string[] {
  return list.filter((entry) => entry !== id);
}
export function addId(list: readonly string[], id: string, max: number): string[] {
  return list.includes(id) || list.length >= max ? [...list] : [...list, id];
}

// ---------------------------------------------------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------------------------------------------------

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
export function dollars(cents: number): string {
  return USD.format(cents / 100);
}
export function wholeDollars(cents: number): string {
  return `${cents < 0 ? "-" : ""}$${Math.abs(Math.round(cents / 100)).toLocaleString("en-US")}`;
}
export function count(n: number): string {
  return n.toLocaleString("en-US");
}
export function plural(n: number, one: string, many: string = `${one}s`): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
/** "2026-10-19" -> "Oct 19". Blank when it is not a day. */
export function shortDay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const name = match === null ? undefined : MONTH_NAMES[Number(match[2]) - 1];
  return match === null || name === undefined ? "" : `${name} ${Number(match[3])}`;
}
/** "2026-10-16" -> "Fri Oct 16". */
export function weekdayDay(iso: string): string {
  const day = shortDay(iso);
  const when = new Date(`${iso}T12:00:00Z`);
  return day === "" || Number.isNaN(when.getTime()) ? day : `${WEEKDAYS[when.getUTCDay()]} ${day}`;
}
export function dayRange(from: string, to: string): string {
  return from === to ? shortDay(from) : `${shortDay(from)} to ${shortDay(to)}`;
}
export function monthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(month);
  const name = match === null ? undefined : MONTH_NAMES[Number(match[2]) - 1];
  return match === null || name === undefined ? month : `${name} ${match[1]}`;
}
export const CHIP = {
  open: (text = "Open day"): Chip => ({ kind: "open", text }),
  exact: (text = "Exact"): Chip => ({ kind: "exact", text }),
  live: (text = "Live"): Chip => ({ kind: "live", text }),
  count: (text: string): Chip => ({ kind: "count", text }),
  partial: (text: string): Chip => ({ kind: "partial", text }),
};
export function failed(emptyTitle = "Could not load"): FigureView {
  return { state: "failed", value: "", context: "", emptyTitle, emptyText: "" };
}
export function failedPreview(): PreviewView {
  return { state: "failed", filters: [], emptyTitle: "Could not load", emptyText: "" };
}
export function ready(value: string, context: string, chip?: Chip): FigureView {
  return { state: "ready", value, context, ...(chip === undefined ? {} : { chip }), emptyTitle: "", emptyText: "" };
}
export function empty(emptyTitle: string, emptyText: string): FigureView {
  return { state: "empty", value: "", context: "", emptyTitle, emptyText };
}
export function emptyPreview(filters: string[], emptyTitle: string, emptyText: string): PreviewView {
  return { state: "empty", filters, emptyTitle, emptyText };
}

/** What a tile with nothing to say yet looks like on a day one location, used by the hosts' own mappers. */
export function isPart(value: unknown): value is Record<string, unknown> & { status: string } {
  return record(value) && typeof value["status"] === "string";
}
export function int(value: unknown): number | undefined {
  return typeof value === "number" && Number.isSafeInteger(value) ? value : undefined;
}
export function text(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}
